
import { useState } from 'react';
import { ChevronRight, Bell, Shield, Download, Trash2, LogOut, Palette, User, Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import BottomNavigation from '@/components/BottomNavigation';

const Settings = () => {
  const [notifications, setNotifications] = useState(true);
  const [theme, setTheme] = useState('system');
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isClearDataDialogOpen, setIsClearDataDialogOpen] = useState(false);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
  const { toast } = useToast();

  const handleExportData = () => {
    // Simulate data export
    toast({
      title: "Exporting data...",
      description: "Your data is being prepared for download.",
    });
    setIsExportDialogOpen(false);
  };

  const handleClearData = () => {
    // Simulate data clearing
    toast({
      title: "Data cleared",
      description: "All your data has been successfully deleted.",
    });
    setIsClearDataDialogOpen(false);
  };

  const handleSignOut = () => {
    // Simulate sign out
    toast({
      title: "Signed out",
      description: "You have been successfully signed out.",
    });
    setIsSignOutDialogOpen(false);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="w-5 h-5" />;
      case 'dark':
        return <Moon className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      default:
        return 'System';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-blue to-white">
      <div className="container max-w-md mx-auto px-4 py-8 pb-24">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-deep-blue mb-2">You</h1>
          <p className="text-deep-blue/70">Manage your account and preferences</p>
        </div>

        {/* Profile Section */}
        <div className="pact-card mb-6">
          <div className="flex items-center space-x-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src="" />
              <AvatarFallback className="bg-vibrant-orange text-white text-xl font-semibold">
                U
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-deep-blue">User</h2>
              <p className="text-deep-blue/70">user@example.com</p>
            </div>
            <ChevronRight className="w-5 h-5 text-deep-blue/50" />
          </div>
        </div>

        {/* Settings Sections */}
        <div className="space-y-4">
          {/* Theme Settings */}
          <div className="pact-card">
            <Dialog open={isThemeDialogOpen} onOpenChange={setIsThemeDialogOpen}>
              <DialogTrigger asChild>
                <button className="w-full flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-deep-blue/10 rounded-full flex items-center justify-center">
                      <Palette className="w-5 h-5 text-deep-blue" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-deep-blue">Theme</h3>
                      <p className="text-sm text-deep-blue/70">{getThemeLabel()}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getThemeIcon()}
                    <ChevronRight className="w-5 h-5 text-deep-blue/50" />
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Select theme</DialogTitle>
                  <DialogDescription>
                    Choose how you want the app to look.
                  </DialogDescription>
                </DialogHeader>
                <RadioGroup value={theme} onValueChange={setTheme}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="light" id="light" />
                    <Label htmlFor="light" className="flex items-center space-x-2 cursor-pointer">
                      <Sun className="w-4 h-4" />
                      <span>Light</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="dark" id="dark" />
                    <Label htmlFor="dark" className="flex items-center space-x-2 cursor-pointer">
                      <Moon className="w-4 h-4" />
                      <span>Dark</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="system" id="system" />
                    <Label htmlFor="system" className="flex items-center space-x-2 cursor-pointer">
                      <Monitor className="w-4 h-4" />
                      <span>System</span>
                    </Label>
                  </div>
                </RadioGroup>
                <DialogFooter>
                  <Button onClick={() => setIsThemeDialogOpen(false)}>
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Notifications */}
          <div className="pact-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-deep-blue/10 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-deep-blue" />
                </div>
                <div>
                  <h3 className="font-medium text-deep-blue">Notifications</h3>
                  <p className="text-sm text-deep-blue/70">Receive reminders</p>
                </div>
              </div>
              <Switch
                checked={notifications}
                onCheckedChange={setNotifications}
              />
            </div>
          </div>

          {/* Data and Privacy */}
          <div className="pact-card">
            <button className="w-full flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-deep-blue/10 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-deep-blue" />
                </div>
                <div className="text-left">
                  <h3 className="font-medium text-deep-blue">Data and Privacy</h3>
                  <p className="text-sm text-deep-blue/70">Manage your information</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-deep-blue/50" />
            </button>
          </div>

          {/* Export Data */}
          <div className="pact-card">
            <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
              <DialogTrigger asChild>
                <button className="w-full flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                      <Download className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-deep-blue">Export my data</h3>
                      <p className="text-sm text-deep-blue/70">Download a copy</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-deep-blue/50" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Export data</DialogTitle>
                  <DialogDescription>
                    This will download a copy of all your data in JSON format.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleExportData}>
                    Export
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Clear Data */}
          <div className="pact-card">
            <Dialog open={isClearDataDialogOpen} onOpenChange={setIsClearDataDialogOpen}>
              <DialogTrigger asChild>
                <button className="w-full flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-deep-blue">Clear all data</h3>
                      <p className="text-sm text-deep-blue/70">Irreversible action</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-deep-blue/50" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Clear all data?</DialogTitle>
                  <DialogDescription>
                    This action cannot be undone. All your PACTs, progress and history will be permanently deleted.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsClearDataDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleClearData}>
                    Clear all
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Sign Out */}
          <div className="pact-card">
            <Dialog open={isSignOutDialogOpen} onOpenChange={setIsSignOutDialogOpen}>
              <DialogTrigger asChild>
                <button className="w-full flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-500/10 rounded-full flex items-center justify-center">
                      <LogOut className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-deep-blue">Sign out</h3>
                      <p className="text-sm text-deep-blue/70">Exit the application</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-deep-blue/50" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Sign out?</DialogTitle>
                  <DialogDescription>
                    You will need to sign in again to access your account.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsSignOutDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleSignOut}>
                    Sign out
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Settings;
